import { useSuspenseQuery } from '@tanstack/react-query';

interface CanvaApi {
    editor?: { fileType: string };
    createDesign: (opts: CreateDesignOptions) => void;
    editDesign: (opts: EditDesignOptions) => void;
}

interface CreateDesignOptions {
    design: {
        type: string;
        title?: string;
        dimensions?: {
            units?: string;
            height?: number;
            width?: number;
        };
    } & CanvaCallbacks &
        CanvaEditor;
}

interface EditDesignOptions {
    design: {
        id: string;
    } & CanvaCallbacks &
        CanvaEditor;
}

interface CanvaCallbacks {
    onDesignOpen?: (opts: { designId: string; designTitle?: string }) => void;
    onDesignPublish?: (opts: {
        designId: string;
        exportUrl: string;
        designTitle?: string;
    }) => Promise<void> | void;
    onDesignClose?: () => void;
}

interface CanvaEditor {
    editor?: {
        publishLabel?: string;
        fileType?: string;
    };
}

const useCanva = () => {
    return useSuspenseQuery({
        queryKey: ['canva'],
        queryFn: (): Promise<CanvaApi | null> => {
            if (
                import.meta.env.VITE_DASHBOARD_CANVA_API_KEY &&
                window.Canva?.DesignButton?.initialize
            ) {
                try {
                    return window.Canva.DesignButton.initialize({
                        apiKey: import.meta.env.VITE_DASHBOARD_CANVA_API_KEY,
                    });
                } catch (e) {
                    Promise.resolve(null);
                }
            }

            return Promise.resolve(null);
        },
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};

export { useCanva };
export type { CanvaApi, CanvaCallbacks, CanvaEditor, EditDesignOptions, CreateDesignOptions };
